import React, { createContext, useState, useContext, useEffect } from 'react';
import { BOOKMAKERS_LIST, DEFAULT_OFF_BOOKS } from '../utils/constants';

// Books hidden everywhere while their data is being evaluated for inclusion
// in bestBook calculations. They never appear in columns, the Settings UI, or
// any local best-book computation. Re-enable by removing entries from this Set.
const HIDDEN_BOOKS = new Set([
  'polymarket',
  'hardrockbet_oh', 'hardrockbet_az', 'hardrockbet_fl',
]);
const isHidden = (book) => HIDDEN_BOOKS.has(String(book).toLowerCase());

const BooksContext = createContext();

export const useBooks = () => {
  const context = useContext(BooksContext);
  if (!context) {
    throw new Error('useBooks must be used within a BooksProvider');
  }
  return context;
};

export const BooksProvider = ({ children }) => {
  const [enabledBooks, setEnabledBooks] = useState(() => {
    // Load saved preferences from localStorage
    const saved = localStorage.getItem('enabledBooks');
    let enabledBooksState = {};

    if (saved) {
      try {
        enabledBooksState = JSON.parse(saved);
      } catch (e) {
        console.error('Error parsing saved books:', e);
      }
    }

    // Merge with BOOKMAKERS_LIST to add any new books that aren't in localStorage.
    // Default new books to enabled — except books listed in DEFAULT_OFF_BOOKS
    // (e.g. ProphetX, Novig, Fliff) which ship visible but disabled and require
    // explicit opt-in from Settings.
    BOOKMAKERS_LIST.forEach(book => {
      if (!(book in enabledBooksState)) {
        enabledBooksState[book] = !DEFAULT_OFF_BOOKS.has(book);
      }
    });

    return enabledBooksState;
  });

  const [enabledBooksForBestBets, setEnabledBooksForBestBets] = useState(() => {
    // Load saved preferences from localStorage
    const saved = localStorage.getItem('enabledBooksForBestBets');
    let enabledBooksState = {};

    if (saved) {
      try {
        enabledBooksState = JSON.parse(saved);
      } catch (e) {
        console.error('Error parsing saved books for best bets:', e);
      }
    } else {
      // MIGRATION: If no best bets settings exist, inherit from main enabledBooks
      const mainBooksSaved = localStorage.getItem('enabledBooks');
      if (mainBooksSaved) {
        try {
          enabledBooksState = JSON.parse(mainBooksSaved);
        } catch (e) {
          console.error('Error parsing main books for migration:', e);
        }
      }
    }

    // Merge with BOOKMAKERS_LIST to add any new books that aren't in localStorage.
    // Default new books to enabled — except DEFAULT_OFF_BOOKS which ship disabled.
    BOOKMAKERS_LIST.forEach(book => {
      if (!(book in enabledBooksState)) {
        enabledBooksState[book] = !DEFAULT_OFF_BOOKS.has(book);
      }
    });

    return enabledBooksState;
  });

  // Per-book Graphs toggle — controls which books appear in line-movement charts.
  const [enabledBooksForGraphs, setEnabledBooksForGraphs] = useState(() => {
    const saved = localStorage.getItem('enabledBooksForGraphs');
    let state = {};
    if (saved) {
      try {
        state = JSON.parse(saved);
      } catch (e) {
        console.error('Error parsing saved books for graphs:', e);
      }
    } else {
      // First-time migration: inherit from main enabledBooks so existing users
      // keep their currently-visible books on graphs without needing to opt-in.
      const mainSaved = localStorage.getItem('enabledBooks');
      if (mainSaved) {
        try {
          state = JSON.parse(mainSaved);
        } catch (e) {
          console.error('Error parsing main books for graphs migration:', e);
        }
      }
    }
    BOOKMAKERS_LIST.forEach(book => {
      if (!(book in state)) {
        state[book] = !DEFAULT_OFF_BOOKS.has(book);
      }
    });
    return state;
  });

  // Best Bets max results settings
  // null means "All" (no limit), number means limit to that many results
  const [bestBetsMaxDesktop, setBestBetsMaxDesktop] = useState(() => {
    const saved = localStorage.getItem('bestBetsMaxDesktop');
    if (saved === null || saved === 'null') return null; // "All"
    const parsed = parseInt(saved, 10);
    return isNaN(parsed) ? null : parsed;
  });

  const [bestBetsMaxMobile, setBestBetsMaxMobile] = useState(() => {
    const saved = localStorage.getItem('bestBetsMaxMobile');
    if (saved === null || saved === 'null') return null; // "All"
    const parsed = parseInt(saved, 10);
    return isNaN(parsed) ? null : parsed;
  });

  // Best Bets American-odds bounds. null on either side = "no bound" that
  // way, so a bet is filtered only when it lands outside the configured
  // range. Two levels: a "global" pair used across all sports, and a
  // per-sport override map. `bestBetsUseGlobalOdds` picks which one wins.
  const readOddsBound = (key) => {
    const saved = localStorage.getItem(key);
    if (saved === null || saved === 'null' || saved === '') return null;
    const parsed = parseInt(saved, 10);
    return Number.isNaN(parsed) ? null : parsed;
  };
  const [bestBetsMinOdds, setBestBetsMinOdds] = useState(() => readOddsBound('bestBetsMinOdds'));
  const [bestBetsMaxOdds, setBestBetsMaxOdds] = useState(() => readOddsBound('bestBetsMaxOdds'));
  const [bestBetsUseGlobalOdds, setBestBetsUseGlobalOdds] = useState(() => {
    const saved = localStorage.getItem('bestBetsUseGlobalOdds');
    // Default true: existing users who never opted in see one shared range.
    return saved === null ? true : saved === 'true';
  });
  const [bestBetsOddsBySport, setBestBetsOddsBySport] = useState(() => {
    const saved = localStorage.getItem('bestBetsOddsBySport');
    if (!saved) return {};
    try {
      const parsed = JSON.parse(saved);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch { return {}; }
  });
  // Update one side (min|max) for a specific sport. Setting the value to null
  // or an empty string clears that side of the bound.
  const setBestBetsSportOdd = (sport, side, value) => {
    if (!sport || (side !== 'min' && side !== 'max')) return;
    const normalized = (value === null || value === '' || value === '-') ? null : value;
    setBestBetsOddsBySport(prev => {
      const next = { ...prev, [sport]: { ...(prev[sport] || {}) } };
      next[sport][side] = normalized;
      return next;
    });
  };
  // Resolve the effective (min, max) range for a sport. Consumers should call
  // this rather than reading the raw state directly.
  const getBestBetsOddsRange = (sport) => {
    if (bestBetsUseGlobalOdds || !sport) {
      return { min: bestBetsMinOdds, max: bestBetsMaxOdds };
    }
    const entry = bestBetsOddsBySport[sport] || {};
    return { min: entry.min ?? null, max: entry.max ?? null };
  };

  // Edge recalculation settings - per-screen toggle
  // false = use server-provided bestBookDiff (all books), true = recalculate using only enabled books
  const [recalcEdgeOdds, setRecalcEdgeOdds] = useState(() => localStorage.getItem('recalcEdgeOdds') === 'true');
  const [recalcEdgeBestBets, setRecalcEdgeBestBets] = useState(() => localStorage.getItem('recalcEdgeBestBets') === 'true');
  const [recalcEdgePlayer, setRecalcEdgePlayer] = useState(() => localStorage.getItem('recalcEdgePlayer') === 'true');

  const [bookOrder, setBookOrder] = useState(() => {
    // Load saved order from localStorage
    const saved = localStorage.getItem('bookOrder');
    let orderState = [];

    if (saved) {
      try {
        orderState = JSON.parse(saved);
      } catch (e) {
        console.error('Error parsing saved book order:', e);
      }
    }

    // If no saved order or empty, use BOOKMAKERS_LIST
    if (!orderState || orderState.length === 0) {
      return [...BOOKMAKERS_LIST];
    }

    // Add any new books from BOOKMAKERS_LIST that aren't in the saved order
    // This ensures new books appear at the end when added
    BOOKMAKERS_LIST.forEach(book => {
      if (!orderState.includes(book)) {
        orderState.push(book);
      }
    });

    return orderState;
  });

  // Save to localStorage whenever enabledBooks, enabledBooksForBestBets, or bookOrder changes
  useEffect(() => {
    localStorage.setItem('enabledBooks', JSON.stringify(enabledBooks));
  }, [enabledBooks]);

  useEffect(() => {
    localStorage.setItem('enabledBooksForBestBets', JSON.stringify(enabledBooksForBestBets));
  }, [enabledBooksForBestBets]);

  useEffect(() => {
    localStorage.setItem('enabledBooksForGraphs', JSON.stringify(enabledBooksForGraphs));
  }, [enabledBooksForGraphs]);

  useEffect(() => {
    localStorage.setItem('bookOrder', JSON.stringify(bookOrder));
  }, [bookOrder]);

  useEffect(() => {
    localStorage.setItem('bestBetsMaxDesktop', bestBetsMaxDesktop === null ? 'null' : String(bestBetsMaxDesktop));
  }, [bestBetsMaxDesktop]);

  useEffect(() => {
    localStorage.setItem('bestBetsMaxMobile', bestBetsMaxMobile === null ? 'null' : String(bestBetsMaxMobile));
  }, [bestBetsMaxMobile]);

  useEffect(() => {
    localStorage.setItem('bestBetsMinOdds', bestBetsMinOdds === null ? 'null' : String(bestBetsMinOdds));
  }, [bestBetsMinOdds]);

  useEffect(() => {
    localStorage.setItem('bestBetsMaxOdds', bestBetsMaxOdds === null ? 'null' : String(bestBetsMaxOdds));
  }, [bestBetsMaxOdds]);

  useEffect(() => {
    localStorage.setItem('bestBetsUseGlobalOdds', String(bestBetsUseGlobalOdds));
  }, [bestBetsUseGlobalOdds]);

  useEffect(() => {
    localStorage.setItem('bestBetsOddsBySport', JSON.stringify(bestBetsOddsBySport));
  }, [bestBetsOddsBySport]);

  useEffect(() => {
    localStorage.setItem('recalcEdgeOdds', String(recalcEdgeOdds));
  }, [recalcEdgeOdds]);

  useEffect(() => {
    localStorage.setItem('recalcEdgeBestBets', String(recalcEdgeBestBets));
  }, [recalcEdgeBestBets]);

  useEffect(() => {
    localStorage.setItem('recalcEdgePlayer', String(recalcEdgePlayer));
  }, [recalcEdgePlayer]);

  const toggleBook = (book) => {
    setEnabledBooks(prev => ({
      ...prev,
      [book]: !prev[book]
    }));
  };

  const toggleBookForBestBets = (book) => {
    setEnabledBooksForBestBets(prev => ({
      ...prev,
      [book]: !prev[book]
    }));
  };

  const toggleBookForGraphs = (book) => {
    setEnabledBooksForGraphs(prev => ({
      ...prev,
      [book]: !prev[book]
    }));
  };

  const isBookEnabled = (book) => {
    if (isHidden(book)) return false;
    return enabledBooks[book] !== false; // Default to true if not explicitly set
  };

  const isBookEnabledForBestBets = (book) => {
    if (isHidden(book)) return false;
    return enabledBooksForBestBets[book] !== false; // Default to true if not explicitly set
  };

  const isBookEnabledForGraphs = (book) => {
    if (isHidden(book)) return false;
    return enabledBooksForGraphs[book] !== false; // Default to true if not explicitly set
  };

  const getEnabledBooks = () => {
    return bookOrder.filter(book => !isHidden(book) && isBookEnabled(book));
  };

  const getEnabledBooksForBestBets = () => {
    return bookOrder.filter(book => !isHidden(book) && isBookEnabledForBestBets(book));
  };

  const getEnabledBooksForGraphs = () => {
    return bookOrder.filter(book => !isHidden(book) && isBookEnabledForGraphs(book));
  };
  
  const reorderBooks = (startIndex, endIndex) => {
    const result = Array.from(bookOrder);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    setBookOrder(result);
  };

  // Expose a filtered view of bookOrder so consumers iterating it for column
  // rendering / Settings UI also skip hidden books. Underlying state still
  // includes them in case HIDDEN_BOOKS is loosened later.
  const visibleBookOrder = bookOrder.filter(book => !isHidden(book));

  return (
    <BooksContext.Provider value={{
      enabledBooks,
      enabledBooksForBestBets,
      enabledBooksForGraphs,
      bookOrder: visibleBookOrder,
      toggleBook,
      toggleBookForBestBets,
      toggleBookForGraphs,
      isBookEnabled,
      isBookEnabledForBestBets,
      isBookEnabledForGraphs,
      getEnabledBooks,
      getEnabledBooksForBestBets,
      getEnabledBooksForGraphs,
      reorderBooks,
      bestBetsMaxDesktop,
      bestBetsMaxMobile,
      setBestBetsMaxDesktop,
      setBestBetsMaxMobile,
      bestBetsMinOdds,
      bestBetsMaxOdds,
      setBestBetsMinOdds,
      setBestBetsMaxOdds,
      bestBetsUseGlobalOdds,
      setBestBetsUseGlobalOdds,
      bestBetsOddsBySport,
      setBestBetsSportOdd,
      getBestBetsOddsRange,
      recalcEdgeOdds,
      setRecalcEdgeOdds,
      recalcEdgeBestBets,
      setRecalcEdgeBestBets,
      recalcEdgePlayer,
      setRecalcEdgePlayer
    }}>
      {children}
    </BooksContext.Provider>
  );
};